import { Controller, useForm } from "react-hook-form";
import { useState, useEffect } from "react";
import { InputText } from "primereact/inputtext";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Dropdown } from "primereact/dropdown";
import { yupResolver } from "@hookform/resolvers/yup";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import * as Yup from "yup";
import { classNames as cx } from "primereact/utils";

import { DCSQueryClient } from "../../../../../../queryClient";
import { useToast } from "../../../../../../Providers/ToastProvider";
import { requests } from "../../../../../../services/http-common";
import { ContractStatusEnum } from "../../../Contracts.model";

import { LabelValue } from "../../../../../../components/LabelValue/LabelValue";

import classes from "./Status.module.scss";
import { onlyLettersAndSpacesRegex } from "../../../../../../constants/regex";
import { RadioButton } from "primereact/radiobutton";
import { useUser } from "../../../../../../keycloak";
import { UserRoles } from "../../../../../../constants/userRoles";

const selectedStatusLabels: { [key in ContractStatusEnum]: string } = {
  [ContractStatusEnum.Draft]: "Draft",
  [ContractStatusEnum.Pending]: "Pending",
  [ContractStatusEnum.Approved]: "Approved",
  [ContractStatusEnum.Denied]: "Denied",
  [ContractStatusEnum.Sent]: "Sent",
  [ContractStatusEnum.Completed]: "Completed",
};

// Status transition options based on current status
const getStatusOptions = (currentStatus: ContractStatusEnum) => {
  switch (currentStatus) {
    case ContractStatusEnum.Draft:
      return [
        { label: "Draft", value: ContractStatusEnum.Draft, disabled: true },
      ];
    case ContractStatusEnum.Pending:
      return [
        { label: "Pending", value: ContractStatusEnum.Pending, disabled: true },
        {
          label: "Approve",
          value: ContractStatusEnum.Approved,
          disabled: false,
        },
        { label: "Deny", value: ContractStatusEnum.Denied, disabled: false },
      ];
    case ContractStatusEnum.Approved:
      return [
        {
          label: "Approved",
          value: ContractStatusEnum.Approved,
          disabled: true,
        },
        { label: 'Revise', value: 'REVISE', disabled: false },
        { label: "Deny", value: ContractStatusEnum.Denied, disabled: false },
      ];
    case ContractStatusEnum.Denied:
      return [
        { label: "Denied", value: ContractStatusEnum.Denied, disabled: true },
        {
          label: "Revise (Back to Pending)",
          value: ContractStatusEnum.Pending,
          disabled: false,
        },
      ];
    case ContractStatusEnum.Sent:
      return [
        { label: 'Sent', value: ContractStatusEnum.Sent, disabled: true },
        { label: 'Revise', value: 'REVISE', disabled: false },
      ];
    case ContractStatusEnum.Completed:
      return [
        { label: 'Completed', value: ContractStatusEnum.Completed, disabled: true },
      ];
    default:
      return [
        { label: "Pending", value: ContractStatusEnum.Pending, disabled: true },
      ];
  }
};

const payTypeRadios = [
  { name: "Pay by ACH", value: "ACH" },
  { name: "Pay with CC", value: "CC" },
  { name: "Please invoice me (I will pay by check)", value: "CHECK" },
];

export const Status = (rowData: {
  status: ContractStatusEnum;
  id: string;
  sendStatus: boolean;
}) => {
  const { id } = rowData;
  const [status, setStatus] = useState<ContractStatusEnum>(rowData.status);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [esignStatus, setEsignStatus] = useState<boolean>(false);
  const [contractStatusOptions, setContractStatusOptions] = useState<any>(
    getStatusOptions(rowData.status),
  );
  const user: any = useUser();

  const { show } = useToast();

  const [showReviseConfirm, setShowReviseConfirm] = useState<boolean>(false);

  const reviseContract = useMutation({
    mutationFn: () => requests.post(`/contract/${rowData.id}/revise`, {}),
    onSuccess: () => {
      setShowReviseConfirm(false);
      show({ detail: 'Contract returned to Pending for revision' });
      setStatus(ContractStatusEnum.Pending);
      setContractStatusOptions(getStatusOptions(ContractStatusEnum.Pending));
      DCSQueryClient.invalidateQueries({ queryKey: ['contract-list'] });
    },
    onError: (error: any) => {
      setShowReviseConfirm(false);
      show({
        severity: 'error',
        detail: error.response?.data?.error?.message || 'Failed to revise contract',
      });
    },
  });

  useQuery({
    queryKey: [`contract-form-${id}`],
    queryFn: () => {
      return requests.get(`/contract/${id}`);
    },
    onSuccess: (data) => {
      if (data.esignStatus) {
        setEsignStatus(true);
      }
    },
  });

  useEffect(() => {
    if (!user) {
      return;
    }

    // Get status options based on current status
    const options = getStatusOptions(status);

    // Filter options for clients - they can't approve/send
    const filteredStatusOptions =
      user.role === "CLIENT"
        ? options.filter(
            (option) =>
              option.value !== ContractStatusEnum.Approved &&
              option.value !== ContractStatusEnum.Sent,
          )
        : options;

    setContractStatusOptions(filteredStatusOptions);
  }, [user, status]);

  const formSchema = Yup.object().shape({
    nameOnAccount: Yup.string().when("payType", {
      is: "ACH",
      then: Yup.string().required("Field is required"),
    }),
    bankAccountType: Yup.string().when("payType", {
      is: "ACH",
      then: Yup.string().required("Field is required"),
    }),
    routingNumber: Yup.string().when("payType", {
      is: "ACH",
      then: Yup.string().required("Field is required"),
    }),
    accountNumber: Yup.string().when("payType", {
      is: "ACH",
      then: Yup.string().required("Field is required"),
    }),
    payType: Yup.string(),
  });

  const hookForm = useForm<{
    nameOnAccount: string;
    bankAccountType: string;
    routingNumber: string;
    accountNumber: string;
    payType: string;
  }>({
    resolver: yupResolver(formSchema),
    shouldFocusError: false,
    defaultValues: {
      payType: "ACH",
    },
  });

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = hookForm;

  const payType = watch("payType");

  const getFormErrorMessage = (name: any) => {
    return (
      // ✅ THIS IS OK
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      errors[name] && <small className="p-error">{errors[name].message}</small>
    );
  };

  const updateStatus = useMutation({
    mutationFn: (newStatus: any) => {
      return requests.put(`/contract/status/${rowData.id}`, { ...newStatus });
    },
    onSuccess: () => {
      show({ detail: "Status updated !" });
      DCSQueryClient.invalidateQueries({ queryKey: [`contract-list`] });
    },
    onError: (error: any) => {
      show({
        severity: "error",
        detail: error.response?.data?.error?.message || "Something went wrong ",
      });
      setStatus(rowData.status);
    },
  });

  const paymentTempCall = useMutation({
    mutationFn: (data: any) => {
      return requests.patch(`/contract/${rowData.id}`, data);
    },
    onSuccess: () => {
      setShowForm(false);
      show({
        detail: "Payment information updated",
      });
    },
    onError: (error: any) => {
      setShowForm(false);
      show({
        severity: "error",
        detail: error.response?.data?.error?.message || "Something went wrong ",
      });
    },
  });

  const onStatusChange = (e: any) => {
    const value = e.target.value;
    if (value === 'REVISE') {
      setShowReviseConfirm(true);
      return;
    }
    updateStatus.mutate({ status: value });
    setStatus(value);
  };

  const onSubmit = (data: any) => {
    paymentTempCall.mutate(data);
  };

  const valueTemplate = (
    option: {
      label: string;
      value: ContractStatusEnum | 'REVISE';
    } | null,
  ) => {
    if (!option || !option.value) {
      return selectedStatusLabels[status] || status || "Unknown";
    }
    if (option.value === 'REVISE') return 'Revise';
    return selectedStatusLabels[option.value];
  };

  useEffect(() => {
    if (rowData.status) {
      setStatus(rowData.status);
      setContractStatusOptions(getStatusOptions(rowData.status));
    } else {
      // Set default status if undefined
      setStatus(ContractStatusEnum.Pending);
      setContractStatusOptions(getStatusOptions(ContractStatusEnum.Pending));
    }
  }, [rowData.status]);

  return (
    <>
      <Dialog
        visible={showForm}
        onHide={() => setShowForm(false)}
        resizable={false}
        draggable={false}
        header="Select Payment Method"
      >
        <form className={classes.form} onSubmit={handleSubmit(onSubmit)}>
          <div className={classes.radios}>
            {payTypeRadios.map((type) => {
              return (
                <div key={type.value} className={classes.radioContainer}>
                  <Controller
                    name="payType"
                    control={control}
                    render={({ field }) => {
                      return (
                        <RadioButton
                          inputId={type.value}
                          {...field}
                          value={type.value}
                          checked={field.value === type.value}
                        />
                      );
                    }}
                  />
                  <label htmlFor={type.value}>{type.name}</label>
                </div>
              );
            })}
          </div>
          {payType === "ACH" ? (
            <>
              <LabelValue label="Name on account" isRequired>
                <Controller
                  name="nameOnAccount"
                  render={({ field }) => (
                    <InputText
                      {...field}
                      keyfilter={onlyLettersAndSpacesRegex}
                      value={field.value}
                    />
                  )}
                  control={control}
                />
                {getFormErrorMessage("nameOnAccount")}
              </LabelValue>
              <LabelValue label="Bank account type" isRequired>
                <Controller
                  name="bankAccountType"
                  render={({ field }) => (
                    <InputText
                      {...field}
                      keyfilter={onlyLettersAndSpacesRegex}
                      value={field.value}
                    />
                  )}
                  control={control}
                />
                {getFormErrorMessage("bankAccountType")}
              </LabelValue>
              <LabelValue label="Routing number" isRequired>
                <Controller
                  name="routingNumber"
                  render={({ field }) => (
                    <InputText {...field} keyfilter="int" value={field.value} />
                  )}
                  control={control}
                />
                {getFormErrorMessage("routingNumber")}
              </LabelValue>
              <LabelValue label="Account number" isRequired>
                <Controller
                  name="accountNumber"
                  render={({ field }) => (
                    <InputText {...field} keyfilter="int" value={field.value} />
                  )}
                  control={control}
                />
                {getFormErrorMessage("accountNumber")}
              </LabelValue>
            </>
          ) : null}
          <Button
            onClick={handleSubmit(onSubmit)}
            label="Submit"
            aria-label="create-lead"
            className={cx("p-button-sm", classes.approveBtn)}
          />
        </form>
      </Dialog>
      <Dropdown
        appendTo="self"
        options={contractStatusOptions}
        onChange={onStatusChange}
        value={status}
        style={{ minWidth: "140px" }}
        className={
          status && selectedStatusLabels[status]
            ? classes[selectedStatusLabels[status]]
            : ""
        }
        id="status"
        disabled={
          status === ContractStatusEnum.Completed ||
          !user?.roles.dcs_client.includes(UserRoles.CONTRACT_UPDATE)
        }
        valueTemplate={valueTemplate}
      />
      <Dialog
        visible={showReviseConfirm}
        onHide={() => setShowReviseConfirm(false)}
        resizable={false}
        draggable={false}
        header="Revise Contract?"
      >
        <p style={{ marginBottom: '1.5rem' }}>
          This will return the contract to Pending. The existing signature flow will be kept
          for history, and a new signature flow will be created when the revised contract is sent.
        </p>
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <Button
            label="Cancel"
            className="p-button-outlined p-button-secondary p-button-sm"
            onClick={() => setShowReviseConfirm(false)}
          />
          <Button
            label="Confirm Revise"
            className="p-button-danger p-button-sm"
            loading={reviseContract.isLoading}
            onClick={() => reviseContract.mutate()}
          />
        </div>
      </Dialog>
    </>
  );
};
